import { useLocation } from 'react-router-dom';
import { TrendingDown, ShieldCheck, Handshake, Building2, Users, Factory, HeartPulse, Truck, ShoppingBag } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { isEnglishPath } from '../utils/i18nRoutes';

const industriesDe = [
  { icon: Factory, label: 'Fertigung & Produktion' },
  { icon: HeartPulse, label: 'Gesundheitswesen' },
  { icon: Truck, label: 'Logistik & Transport' },
  { icon: ShoppingBag, label: 'Handel & E-Commerce' },
];

const industriesEn = [
  { icon: Factory, label: 'Manufacturing & Production' },
  { icon: HeartPulse, label: 'Healthcare' },
  { icon: Truck, label: 'Logistics & Transport' },
  { icon: ShoppingBag, label: 'Retail & E-Commerce' },
];

const principlesDe = [
  {
    icon: TrendingDown,
    title: 'Erfolgsbasiert vergütet',
    description:
      'Wir verdienen nur, wenn Sie messbar sparen oder einsparen. Die Vergütung orientiert sich an den nachweislich erzielten Einsparungen, nicht an Stundensätzen oder Beratungstagen.',
  },
  {
    icon: ShieldCheck,
    title: 'Kein Risiko für Sie',
    description:
      'In der Analyse- und Strategiephase entstehen keine Kosten für Sie. Erst wenn wir gemeinsam Maßnahmen umsetzen und diese Ergebnisse liefern, beteiligen wir uns am Erfolg.',
  },
  {
    icon: Handshake,
    title: 'Interessen parallel ausgerichtet',
    description:
      'Weil unsere Vergütung an Ihren Erfolg geknüpft ist, arbeiten wir an den Maßnahmen mit dem größten Nutzen für Ihr Unternehmen – nicht an denen, die uns am besten bezahlen.',
  },
];

const principlesEn = [
  {
    icon: TrendingDown,
    title: 'Success-based compensation',
    description:
      'We only earn when you measurably save or cut costs. Our compensation is based on the demonstrably achieved savings, not on hourly rates or consulting days.',
  },
  {
    icon: ShieldCheck,
    title: 'No risk for you',
    description:
      'No costs arise for you during the analysis and strategy phase. Only when we jointly implement measures and they deliver results do we share in the success.',
  },
  {
    icon: Handshake,
    title: 'Interests aligned',
    description:
      'Because our compensation is tied to your success, we work on the measures with the greatest benefit for your company – not on the ones that pay us best.',
  },
];

export default function WhyUs() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);
  const industries = isEn ? industriesEn : industriesDe;
  const principles = isEn ? principlesEn : principlesDe;

  return (
    <section id="why-us" className="py-20 px-6 bg-soft dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Target audience */}
        <div ref={headerRef} className="text-center mb-12">
          <h2
            className={`text-2xl sm:text-3xl md:text-4xl font-bold text-dark dark:text-white mb-4 ${
              headerVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            {isEn ? 'Who we work for' : 'Für wen wir arbeiten'}
          </h2>
          <p
            className={`text-lg text-muted dark:text-gray-300 max-w-3xl mx-auto ${
              headerVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
            style={{ animationDelay: '0.1s' }}
          >
            {isEn
              ? 'OptiAI works with mid-sized companies from Austria, Germany, and Switzerland. Our clients have between 50 and 500 employees and monthly operating expenses starting at 20,000 euros in areas such as energy, IT, or telecommunications.'
              : 'OptiAI arbeitet mit mittelständischen Unternehmen aus Österreich, Deutschland und der Schweiz. Unsere Kunden beschäftigen zwischen 50 und 500 Mitarbeiter und haben monatliche Betriebsausgaben ab 20.000 Euro in Bereichen wie Energie, IT oder Telekommunikation.'}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {industries.map((industry) => (
            <div
              key={industry.label}
              className="card-base p-5 flex flex-col items-center text-center"
            >
              <div className="w-12 h-12 bg-primary-tint rounded-xl flex items-center justify-center mb-3">
                <industry.icon className="w-6 h-6 text-primary" />
              </div>
              <span className="font-semibold text-dark dark:text-white text-sm">{industry.label}</span>
            </div>
          ))}
        </div>

        {/* Success-based compensation */}
        <div className="text-center mb-10">
          <h3 className="text-2xl sm:text-3xl font-bold text-dark dark:text-white mb-4">
            {isEn ? 'Success-based compensation' : 'Erfolgsbasierte Vergütung'}
          </h3>
          <p className="text-lg text-muted dark:text-gray-300 max-w-3xl mx-auto">
            {isEn
              ? 'OptiAI does not work with the classic consulting model of daily rates. Our compensation is based on the success we achieve for your company. This means: we only earn when you measurably benefit.'
              : 'OptiAI arbeitet nicht nach dem klassischen Beratermodell mit Tagessätzen. Unsere Vergütung richtet sich nach dem Erfolg, den wir für Ihr Unternehmen erzielen. Das bedeutet: Wir verdienen nur, wenn Sie messbar profitieren.'}
          </p>
        </div>

        {/* Two-column: text+tags left, fact cards right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="space-y-6">
            <div className="flex items-start gap-4 p-6 bg-white dark:bg-gray-800 rounded-card border border-line dark:border-gray-700">
              <div className="w-12 h-12 bg-primary-tint rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h4 className="font-bold text-dark dark:text-white mb-2 flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  {isEn ? 'Company Size' : 'Unternehmensgröße'}
                </h4>
                <p className="text-muted dark:text-gray-300 leading-relaxed text-sm">
                  {isEn
                    ? 'The focus is on companies with 100 to 500 employees. Companies with fewer than 50 employees typically do not benefit sufficiently from the economies of scale of automation. Companies with more than 500 employees often have their own purchasing or IT departments that already cover the relevant processes. If in doubt, we clarify in a brief conversation whether a collaboration makes sense for your company.'
                    : 'Der Schwerpunkt liegt auf Firmen mit 100 bis 500 Beschäftigten. Unternehmen mit weniger als 50 Mitarbeitern profitieren in der Regel nicht ausreichend vom Skaleneffekt der Automatisierung. Unternehmen mit über 500 Mitarbeitern haben oft eigene Einkaufs- oder IT-Abteilungen, die die relevanten Prozesse bereits abdecken. Im Zweifelsfall klären wir in einem kurzen Gespräch, ob eine Zusammenarbeit für Ihr Unternehmen sinnvoll ist.'}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {principles.map((principle) => (
              <div
                key={principle.title}
                className="bg-white dark:bg-gray-800 rounded-card p-6 border-l-4 border-accent border border-line dark:border-gray-700"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-tint rounded-lg flex items-center justify-center flex-shrink-0">
                    <principle.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-dark dark:text-white mb-2">{principle.title}</h4>
                    <p className="text-muted dark:text-gray-300 leading-relaxed text-sm">{principle.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
